/**
 * The email thread's conversation (outlook-orachat-uplift s6; design C33, C34;
 * plan AD-1, AD-11).
 *
 *  - The conversation id of a thread is NEVER derived here. The pane asks the
 *    server (`POST /api/workflows/orachat/threads/resolve`, through the Product
 *    API), which derives it from the authenticated caller, the mailbox and the
 *    thread (C33). The add-in only says which thread: the Office
 *    `conversationId` when the item has one, else (reading pane) its internet
 *    message id. A compose window without a conversationId (a new message) has
 *    no thread: it gets a fresh conversation for this window, and so does any
 *    item whose thread cannot be resolved (AD-1).
 *  - `currentThread` is the item the pane MOUNTED its panel for — the resolved
 *    id or the AD-1 fallback, whichever it mounted — read by the draft tools'
 *    guard. It is cleared the moment the open item changes, before any new
 *    resolve is sent, so a draft tool never acts on an email its turn did not
 *    start on (C34). Never the previous item's: null until the new panel mounts.
 *
 * Light on purpose: `useMailboxItem` imports this module (its ItemChanged
 * handler clears `currentThread`), so it never imports the panel.
 */

import { getApiBaseUrl } from "../config/apiBaseUrl";
import { newCorrelationId } from "../obs/logger";
import { getChatToken } from "../oraChat/bootOraChat";
import { addinCallHeaders, addinFetch } from "../oraChat/addinRequest";
import { SignInRequiredError } from "../services/authClient";

/** The Product API pass-through to the substrate's thread resolve. */
export const THREAD_RESOLVE_PATH = "/api/workflows/orachat/threads/resolve";

/** The pane a thread is resolved from. Only a compose resolve claims a handoff (C35). */
export type ThreadPane = "read" | "compose";

/** What the pane knows about the open item. A read item's EmailContext is one. */
export interface ThreadItem {
  emailId?: string;
  internetMessageId?: string;
  /** Office's `item.conversationId`: the thread. */
  conversationId?: string | null;
  mailboxAddress?: string;
}

/** Which thread the pane is on, and the key it mounts and guards by. */
export interface ThreadKey {
  /**
   * The open item as the pane keys its panel (`turnTag`) and the draft tools'
   * guard. Never sent to the server and never a conversation id.
   */
  itemKey: string;
  conversationKey: string | null;
  internetMessageId: string | null;
  mailboxAddress: string | null;
}

/**
 * A compose window's one item key. A task pane hosts one draft for its life
 * (each window is its own page), so a compose window has exactly one
 * conversation (C34 "per window"); its fallback id is minted per window.
 */
export const COMPOSE_WINDOW_KEY = "compose-window";

function nonEmpty(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/** The thread facts of the open COMPOSE item, read from Office (sync properties). */
export function readComposeThreadItem(): ThreadItem {
  try {
    const mailbox = (
      globalThis as {
        Office?: {
          context?: {
            mailbox?: { item?: { conversationId?: unknown }; userProfile?: { emailAddress?: unknown } };
          };
        };
      }
    ).Office?.context?.mailbox;
    return {
      conversationId: nonEmpty(mailbox?.item?.conversationId),
      mailboxAddress: nonEmpty(mailbox?.userProfile?.emailAddress) ?? undefined,
    };
  } catch {
    return {};
  }
}

/** The thread key of the open item in `pane` (pure). */
export function threadKeyFor(pane: ThreadPane, item: ThreadItem | null): ThreadKey {
  if (pane === "compose") {
    return {
      itemKey: COMPOSE_WINDOW_KEY,
      conversationKey: nonEmpty(item?.conversationId),
      // A draft has no internet message id: no conversationId means no thread.
      internetMessageId: null,
      mailboxAddress: nonEmpty(item?.mailboxAddress),
    };
  }
  return {
    itemKey: item?.internetMessageId || item?.emailId || "",
    conversationKey: nonEmpty(item?.conversationId),
    internetMessageId: nonEmpty(item?.internetMessageId),
    mailboxAddress: nonEmpty(item?.mailboxAddress),
  };
}

/** Whether the item names a thread the server can resolve at all. */
export function hasThread(key: ThreadKey): boolean {
  return key.conversationKey !== null || key.internetMessageId !== null;
}

export interface ResolveThreadRequest {
  pane: ThreadPane;
  conversationKey: string | null;
  internetMessageId: string | null;
  mailboxAddress: string | null;
}

/** A compose resolve's claimed handoff (`{kind, hands_free}`), passed through as the server sent it. */
export type ThreadHandoff = Record<string, unknown> | null;

export type ThreadResolution =
  | {
      kind: "resolved";
      conversationId: string;
      existing: boolean;
      lastActivityAt: string | null;
      handoff: ThreadHandoff;
    }
  /** 401 / 403, or no silent token: the S9a sign-in card (AD-11). */
  | { kind: "sign_in" }
  /** 503 `assistant_disabled`: the chat-down state (AD-11). */
  | { kind: "disabled" }
  /** Anything else: a conversation for this window (AD-1), with the named code to log. */
  | { kind: "fallback"; code: string };

/**
 * Ask the server for the caller's conversation on a thread. Never throws.
 *
 * The same call shape as the chat user read (`chatUser.ts`): the chat's silent
 * token, the add-in's header trio, no cookie.
 */
export async function resolveThread(
  req: ResolveThreadRequest,
  fetchImpl?: typeof fetch,
): Promise<ThreadResolution> {
  let token: string;
  try {
    token = await getChatToken();
  } catch (err) {
    return err instanceof SignInRequiredError ? { kind: "sign_in" } : { kind: "fallback", code: "token_unavailable" };
  }

  let res: Response;
  try {
    res = await addinFetch(fetchImpl)(`${getApiBaseUrl()}${THREAD_RESOLVE_PATH}`, {
      method: "POST",
      credentials: "omit",
      headers: { ...addinCallHeaders(token, newCorrelationId()), "Content-Type": "application/json" },
      body: JSON.stringify({
        pane: req.pane,
        ...(req.conversationKey ? { conversation_key: req.conversationKey } : {}),
        ...(req.internetMessageId ? { internet_message_id: req.internetMessageId } : {}),
        ...(req.mailboxAddress ? { mailbox_address: req.mailboxAddress } : {}),
      }),
    });
  } catch {
    return { kind: "fallback", code: "network_or_cors" };
  }

  if (res.status === 401 || res.status === 403) return { kind: "sign_in" };
  let payload: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = await res.json();
    payload = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    payload = null;
  }

  if (res.ok) {
    const conversationId = payload?.conversation_id;
    if (typeof conversationId !== "string" || conversationId === "") {
      return { kind: "fallback", code: "invalid_response" };
    }
    const handoff = payload?.handoff;
    return {
      kind: "resolved",
      conversationId,
      existing: payload?.existing === true,
      lastActivityAt: typeof payload?.last_activity_at === "string" ? payload.last_activity_at : null,
      handoff: handoff && typeof handoff === "object" ? (handoff as Record<string, unknown>) : null,
    };
  }
  const code = typeof payload?.error === "string" ? payload.error : `http_${res.status}`;
  if (res.status === 503 && code === "assistant_disabled") return { kind: "disabled" };
  return { kind: "fallback", code };
}

// ─── The mounted thread (read by the draft tools' guard) ─────────────────────

export interface CurrentThread {
  itemKey: string;
  conversationId: string;
}

let currentThread: CurrentThread | null = null;

/** The item and conversation the pane's panel is mounted for; null while the item is changing. */
export function getCurrentThread(): CurrentThread | null {
  return currentThread;
}

/** The panel mounted for `itemKey` with `conversationId` (resolved or the AD-1 fallback). */
export function setCurrentThread(itemKey: string, conversationId: string): void {
  currentThread = { itemKey, conversationId };
}

/** The open item changed: no draft tool may act until the new panel mounts. Synchronous. */
export function clearCurrentThread(): void {
  currentThread = null;
}
